import { Box, Card, Flex, Text, Tag, TagLabel, TagLeftIcon, Tooltip, useDisclosure } from '@chakra-ui/react'
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  getAllContentStatistics,
  getContentCompletedOverview,
  getContentPendingOverview,
} from '../../api/content/content'
import { CheckCircleFilledIcon, ClockFillIcon } from '../../assets/icons'
import { statusColors } from '../../themes/components/tag'
import PendingUsersModal from '../modals/PendingUsersModal'
import LoadingTable from './base/LoadingTable'
import MasterTable, { Column } from './base/MasterTable'
import Pagination from './base/Pagination'
import SearchInput from './base/SearchInput'
import typeMapping from '../../themes/contentTypeMapping'
import FilterButton from './base/FilterButton'

type ModalType = 'pending' | 'completed'

const ContentStatisticsTable: React.FC = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [pageSize] = useState(15)
  const [search, setSearch] = useState('')
  const [pendingFilter, setPendingFilter] = useState<boolean>(false)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [selectedContent, setSelectedContent] = useState<Record<string, unknown> | null>(null)
  const [modalUsers, setModalUsers] = useState<Record<string, unknown>[]>([])
  const [isLoadingModal, setIsLoadingModal] = useState(false)
  const [modalType, setModalType] = useState<ModalType>('pending')

  const statistics = useQuery({
    queryKey: ['content-statistics', page, pageSize, search, pendingFilter],
    placeholderData: keepPreviousData,
    queryFn: () =>
      getAllContentStatistics({
        page,
        page_size: pageSize,
        search,
        has_pending: pendingFilter,
      }),
    refetchOnWindowFocus: false,
  })

  const handleShowUsers = async (content: Record<string, unknown>, type: ModalType) => {
    setSelectedContent(content)
    setModalType(type)
    setIsLoadingModal(true)
    onOpen()
    try {
      const contentId = String(content.content_id || content.id)
      const data =
        type === 'pending' ? await getContentPendingOverview(contentId) : await getContentCompletedOverview(contentId)
      setModalUsers(data || [])
    } catch (error) {
      console.error(`Failed to fetch ${type} users:`, error)
      setModalUsers([])
    } finally {
      setIsLoadingModal(false)
    }
  }

  const columns: Column[] = [
    {
      label: t('content.fields.title', 'Title'),
      accessor: 'title',
      type: 'custom',
      Cell: ({ row }) => <Text textStyle="l-xs">{row.title || '—'}</Text>,
    },
    {
      label: t('content.fields.topic', 'Topic'),
      accessor: 'topic',
      type: 'custom',
      Cell: ({ row }) => <Text textStyle="l-xs">{row.topic || '—'}</Text>,
    },
    {
      label: t('content.fields.contentType'),
      accessor: 'content_type',
      type: 'custom',
      Cell: ({ row }) => {
        const typeInfo = typeMapping[row.content_type as keyof typeof typeMapping]

        if (!typeInfo) return null

        const typeKey = row.content_type === 'training' ? 'todo.trainAndTest' : `content.types.${row.content_type}`
        const translatedType = t(typeKey)
        const typeLabel = translatedType !== typeKey ? translatedType : typeInfo.label

        return (
          <Tag
            borderRadius="14px"
            h="28px"
            bg={typeInfo?.bg}
            color={typeInfo?.color}
            borderWidth="1px"
            borderColor={typeInfo?.borderColor}
          >
            <TagLeftIcon>{typeInfo?.icon}</TagLeftIcon>
            <TagLabel textStyle="h-xxs" pb={'1px'}>
              {typeLabel}
            </TagLabel>
          </Tag>
        )
      },
    },
    {
      label: t('explore.statistics.assigned'),
      accessor: 'total_assigned',
      type: 'custom',
      Cell: ({ row }) => <Text textStyle="l-xs">{row.total_assigned || 0}</Text>,
    },
    {
      label: t('explore.statistics.pending'),
      accessor: 'pending',
      type: 'custom',
      Cell: ({ row }) => {
        const count = row.pending || 0
        const isClickable = count > 0
        return (
          <Tooltip
            label={isClickable ? t('explore.statistics.showPendingUsers') : ''}
            placement="top"
            hasArrow
            isDisabled={!isClickable}
          >
            <Tag
              borderRadius="14px"
              h="28px"
              color={count > 0 ? statusColors.warning.color : statusColors.success.color}
              bg={count > 0 ? statusColors.warning.bg : statusColors.success.bg}
              borderColor={count > 0 ? statusColors.warning.border : statusColors.success.border}
              borderWidth="1px"
              whiteSpace="nowrap"
              cursor={isClickable ? 'pointer' : 'default'}
              transition="all 0.2s"
              _hover={isClickable ? { opacity: 0.8, transform: 'scale(1.05)' } : {}}
              onClick={
                isClickable
                  ? (e) => {
                      e.stopPropagation()
                      handleShowUsers(row, 'pending')
                    }
                  : undefined
              }
            >
              <TagLeftIcon>{count > 0 ? <ClockFillIcon /> : <CheckCircleFilledIcon />}</TagLeftIcon>
              <TagLabel textStyle="h-xxs">{count}</TagLabel>
            </Tag>
          </Tooltip>
        )
      },
    },
    {
      label: t('explore.statistics.completed'),
      accessor: 'total_completed',
      type: 'custom',
      Cell: ({ row }) => {
        const count = row.total_completed || 0
        const isClickable = count > 0
        return (
          <Tooltip
            label={isClickable ? t('explore.statistics.showCompletedUsers') : ''}
            placement="top"
            hasArrow
            isDisabled={!isClickable}
          >
            <Tag
              borderRadius="14px"
              h="28px"
              color={statusColors.success.color}
              bg={statusColors.success.bg}
              borderColor={statusColors.success.border}
              borderWidth="1px"
              whiteSpace="nowrap"
              cursor={isClickable ? 'pointer' : 'default'}
              transition="all 0.2s"
              _hover={isClickable ? { opacity: 0.8, transform: 'scale(1.05)' } : {}}
              onClick={
                isClickable
                  ? (e) => {
                      e.stopPropagation()
                      handleShowUsers(row, 'completed')
                    }
                  : undefined
              }
            >
              <TagLeftIcon>
                <CheckCircleFilledIcon />
              </TagLeftIcon>
              <TagLabel textStyle="h-xxs">{count}</TagLabel>
            </Tag>
          </Tooltip>
        )
      },
    },
  ]

  const tableData = statistics.data?.data || []
  const totalItems = statistics.data?.count ?? 0
  const computedMaxPage = Math.max(1, Math.ceil(totalItems / pageSize))
  const apiMaxPage =
    typeof statistics.data?.max_page === 'number' && statistics.data.max_page > 0 ? statistics.data.max_page : undefined
  const effectiveMaxPage = apiMaxPage ? Math.min(apiMaxPage, computedMaxPage) : computedMaxPage

  useEffect(() => {
    const nextPage = page + 1
    if (effectiveMaxPage <= page) return

    queryClient
      .prefetchQuery({
        queryKey: ['content-statistics', nextPage, pageSize, search, pendingFilter],
        queryFn: () =>
          getAllContentStatistics({
            page: nextPage,
            page_size: pageSize,
            search,
            has_pending: pendingFilter,
          }),
        staleTime: 30 * 1000,
      })
      .catch(() => {
        // ignore prefetch errors
      })
  }, [page, pageSize, search, pendingFilter, queryClient, effectiveMaxPage])

  const handleSearch = (newSearch: string) => {
    setSearch(newSearch)
    setPage(1)
  }

  useEffect(() => {
    setPage(1)
  }, [search, pendingFilter])

  return (
    <>
      <Card width="100%" p="0">
        <Box>
          <Flex justifyContent="space-between" alignItems="center" height="64px" flexWrap="wrap" gap={4} px="20px">
            <SearchInput onSearch={handleSearch} />
            <Flex
              align="center"
              gap="10px"
              px="14px"
              h="32px"
              borderRadius="8px"
              bg={pendingFilter ? 'purple.400' : 'white'}
              border="1px solid"
              borderColor={pendingFilter ? 'purple.400' : 'solid.100'}
              transition="all 0.2s"
              cursor="pointer"
              onClick={() => setPendingFilter(!pendingFilter)}
              _hover={{
                bg: pendingFilter ? 'purple.500' : 'solid.25',
                borderColor: pendingFilter ? 'purple.500' : 'solid.200',
              }}
            >
              <Text fontSize="13px" color={pendingFilter ? 'white' : 'black'} transition="color 0.2s">
                {t('common.fields.pending', 'Pending')}
              </Text>
              <Box
                w="32px"
                h="18px"
                borderRadius="full"
                bg={pendingFilter ? 'rgba(255,255,255,0.3)' : 'solid.200'}
                position="relative"
                transition="background 0.2s"
                flexShrink={0}
              >
                <Box
                  position="absolute"
                  top="2px"
                  left={pendingFilter ? '16px' : '2px'}
                  w="14px"
                  h="14px"
                  borderRadius="full"
                  bg="white"
                  transition="all 0.2s"
                  boxShadow="0 1px 3px rgba(0,0,0,0.2)"
                />
              </Box>
            </Flex>
          </Flex>

          <Box minHeight="58vh">
            {statistics.isLoading && <LoadingTable columns={columns} />}
            {statistics.data && (
              <MasterTable
                columns={columns}
                data={tableData}
                selectedRows={[]}
                onSelectAll={() => {}}
                onSelectRow={() => {}}
                allSelected={false}
                isIndeterminate={false}
                tableType="content"
              />
            )}
          </Box>
        </Box>

        <Pagination
          currentPage={page}
          totalItems={totalItems}
          itemsPerPage={pageSize}
          maxPage={effectiveMaxPage}
          handlePreviousPage={() => setPage(page - 1)}
          handleNextPage={() => setPage(page + 1)}
          handlePageClick={(newPage) => setPage(newPage)}
        />
      </Card>

      <PendingUsersModal
        isOpen={isOpen}
        onClose={onClose}
        template={selectedContent}
        pendingUsers={modalUsers}
        isLoading={isLoadingModal}
        title={
          modalType === 'completed'
            ? t('explore.statistics.completedUsersTitle')
            : t('explore.statistics.pendingUsersTitle')
        }
        emptyMessage={
          modalType === 'completed' ? t('explore.statistics.noCompletedUsers') : t('explore.statistics.noPendingUsers')
        }
      />
    </>
  )
}

export default ContentStatisticsTable
